import type { ReactNode } from "react";
import {
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { type LucideIcon } from "lucide-react-native";
import { colors } from "../theme/tokens";
import {
  EmptyStateSubtitleText,
  EmptyStateTitleText,
  type EmptyStateTitleVariant,
} from "./EmptyStateText";
import { GradientIcon, type GradientVariant } from "./GradientIcon";

/**
 * Centered content absence: optional illustration, shared empty-state text
 * roles, and an optional action. The caller owns any surrounding card/surface.
 * Title emphasis is explicit, independent of icon treatment;
 * density controls only the footprint, never the text's semantic role.
 * Loading and error states remain separate feature-owned states.
 */
type Props = {
  /** Leading icon. */
  icon?: LucideIcon;
  /** Optional illustration rendered above the title instead of the icon. */
  illustration?: ReactNode;
  /** Title line — required, the main "what's empty" message. */
  title: string;
  /** Compact section message or a stronger featured-state heading. */
  titleVariant?: EmptyStateTitleVariant;
  /** Optional explanation below the title. */
  subtitle?: string;
  /** Optional width constraint; typography belongs to the shared text role. */
  subtitleStyle?: StyleProp<Pick<TextStyle, "width" | "maxWidth">>;
  /** Optional CTA element (button) rendered below the subtitle. */
  action?: ReactNode;
  /** Wrap the icon in a circular tinted background and render the icon
   *  with `GradientIcon`. Defaults true (premium orb look). Pass
   *  `iconOrb={false}` for inline list-empty placeholders where a faded
   *  one-liner reads better than a featured orb. */
  iconOrb?: boolean;
  /** Brand gradient variant for the orb-icon stroke. Defaults to "ember"
   *  (deep brown). Only applies when `iconOrb` is true. */
  gradient?: GradientVariant;
  /** Vertical footprint. "regular" is the featured 28px-padded block;
   *  "compact" reduces padding and gaps for inline section placeholders
   *  (e.g. the stimulation-monitoring empty state) so the card doesn't
   *  dominate the surrounding screen. Prefer this over per-call-site
   *  padding overrides in `style`. */
  density?: "regular" | "compact";
  /** Hue used for the orb's tinted background and the bare-icon stroke.
   *  Defaults to softBrownFaint. The orb icon itself uses `gradient`, not
   *  this color, so the orb bg can be one hue while the icon stroke
   *  stays on-brand. */
  accentColor?: string;
  testID?: string;
  style?: ViewStyle;
};

const ICON_SIZE = 24;
const ORB_SIZE = 56;

export function EmptyState({
  icon: Icon,
  illustration,
  title,
  titleVariant = "compact",
  subtitle,
  subtitleStyle,
  action,
  iconOrb = true,
  gradient = "ember",
  density = "regular",
  accentColor = colors.softBrownFaint,
  testID,
  style,
}: Props) {
  const isCompact = density === "compact";
  return (
    <View
      testID={testID}
      style={[
        {
          alignItems: "center",
          paddingVertical: isCompact ? 14 : 28,
          paddingHorizontal: 16,
          gap: isCompact ? 12 : 16,
        },
        style,
      ]}
    >
      {illustration ? (
        <View>{illustration}</View>
      ) : Icon ? (
        iconOrb ? (
          <View
            style={{
              width: ORB_SIZE,
              height: ORB_SIZE,
              borderRadius: ORB_SIZE / 2,
              backgroundColor: `${accentColor}1A`,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GradientIcon
              icon={Icon}
              size={ICON_SIZE}
              strokeWidth={1.5}
              variant={gradient}
            />
          </View>
        ) : (
          <Icon size={20} color={accentColor} strokeWidth={1.5} />
        )
      ) : null}
      <View style={{ alignItems: "center", gap: 4 }}>
        <EmptyStateTitleText
          variant={titleVariant}
          style={{ textAlign: "center" }}
        >
          {title}
        </EmptyStateTitleText>
        {subtitle ? (
          <EmptyStateSubtitleText
            style={[{ textAlign: "center", marginTop: 0 }, subtitleStyle]}
          >
            {subtitle}
          </EmptyStateSubtitleText>
        ) : null}
      </View>
      {action ? <View>{action}</View> : null}
    </View>
  );
}
